import type { ForwardRefExoticComponent, SVGProps } from "react"
import { type IconType } from "../../icon"
import Image from "next/image"

export type FeatureItemVariant =
  | "3xl-stacked-centre" // extra large (either) icon in coloured square, larger heading and text with all 3 stacked and centred.
  | "2xl-stacked-left" // very large (either) icon in coloured square, larger heading and text with all 3 stacked and left-aligned. --- default
  | "2xl-stacked-centre" // very large (either) icon in coloured square, larger heading and text with all 3 stacked and centred.
  | "xl" // very large OUTLINE icon in coloured square in line with larger heading, text below.
  | "single" // small SOLID icon off to side, heading and text on same line

export type FeatureItemProps = {
  title?: string | JSX.Element
  titleColour?: string
  titleClass?: string
  body?: string | JSX.Element
  bodyColour?: string

  icon?: IconType | string | ForwardRefExoticComponent<SVGProps<SVGSVGElement> & { title?: string; titleId?: string }> | undefined // string is an image
  iconColour?: string
  iconBgColour?: string // only used for large and larger icons (white icon w/ background)
  iconCentre?: boolean // for stacked variants, where icon should be centred along with text (text gets centred by centred grid layouts)
  iconSize?: string // there may be different icon size - only used for 3xl large icon for now

  disableHover?: boolean
  hoverClass?: string
  itemClass?: string // applied to the item div
}

const FeatureItem = (props: FeatureItemProps & { variant?: FeatureItemVariant }) => {
  const hoverClass = `group transition-colors duration-200  ${props.hoverClass ? `${props.hoverClass}` : "hover:bg-black/15"} `
  const titleClass = `transition-colors duration-200 ${props.titleClass ?? ""} ${props.titleColour ?? "text-white"} font-bold text-resp-lg`
  const iconColour = props.iconColour ?? "text-white"
  const bodyColour = props.bodyColour ?? "text-gray-300"

  switch (props.variant) {
    case "single":
      return (
        <div className={`relative p-4 rounded-xl ${hoverClass} ${props.itemClass ?? ""} flex`}>
          {props.icon && (
            <div className="mr-4 mt-1">
              {typeof props.icon === "string" ? (
                <Image src={props.icon} alt="" height={20} width={20} className="h-5 w-5 max-w-auto" />
              ) : (
                <props.icon className={`h-5 w-5 ${iconColour}`} aria-hidden="true" />
              )}
            </div>
          )}
          <div className="text-resp-base leading-7 flex-grow">
            <dt className={`inline font-semibold ${titleClass}`}>{props.title}</dt> <dd className={`inline ${bodyColour}`}>{props.body}</dd>
          </div>
        </div>
      )
    // case "xl":
    //   return (
    //     <div className={`rounded-xl p-4 ${hoverClass} ${props.itemClass ?? ""} text-start text-white`}>
    //       <div className="mb-2 flex items-center gap-2">
    //         {props.icon && (
    //           <div className={`flex h-fit w-fit items-center rounded-md ${props.iconBgColour ?? "bg-green"}`}>
    //             {typeof props.icon === "string" ? (
    //               <div className="relative m-2 h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 lg:h-14 lg:w-14">
    //                 <Image src={props.icon} alt="" fill />
    //               </div>
    //             ) : (
    //               <props.icon className={`m-2 h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 ${iconColour}`} />
    //             )}
    //           </div>
    //         )}

    //         {typeof props.title === "string" ? <h2 className={titleClass}>{props.title}</h2> : props.title && <div className={titleClass}>{props.title}</div>}
    //       </div>

    //       <div className={`text-resp-lg ${bodyColour}`}>{props.body}</div>
    //     </div>
    //   )
    case "xl":
      return (
        <div className={`sm:flex p-3 sm:p-6 rounded-xl ${hoverClass} ${props.itemClass ?? ""} text-white`}>
          {props.icon && (
            <div className={`flex mx-auto sm:ml-0 sm:mr-6 mb-4 sm:mb-0 h-fit w-fit items-center justify-center rounded-lg ${props.iconBgColour ?? ""}`}>
              {props.icon &&
                (typeof props.icon === "string" ? (
                  <div className="relative h-6 sm:h-8 md:h-10 w-6 sm:w-8 md:w-10 m-2">
                    <Image src={props.icon} alt="" fill />
                  </div>
                ) : (
                  <props.icon className={`h-6 sm:h-8 md:h-10 w-6 sm:w-8 md:w-10 m-2`} aria-hidden="true" />
                ))}
            </div>
          )}
          <div className="flex-grow text-center sm:text-start">
            <dt className="text-resp-lg font-semibold leading-7">
              <span className={`${titleClass}`}>{props.title}</span>
            </dt>
            <dd className={`mt-2 text-resp-base leading-7 ${bodyColour}`}>{props.body}</dd>
          </div>
        </div>
      )
    case "2xl-stacked-left":
      return (
        <div className={`rounded-xl p-4 ${hoverClass} ${props.itemClass ?? ""} text-start`}>
          {props.icon && (
            <div className={`mb-2 flex h-fit w-fit items-center rounded-md sm:mb-4 ${props.iconBgColour ?? "bg-green"}`}>
              {typeof props.icon === "string" ? (
                <div className="relative m-2 h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 lg:h-14 lg:w-14">
                  <Image src={props.icon} alt="" fill />
                </div>
              ) : (
                <props.icon className={`m-2 h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 ${iconColour}`} />
              )}
            </div>
          )}

          {typeof props.title === "string" ? <h2 className={titleClass}>{props.title}</h2> : props.title && <div className={titleClass}>{props.title}</div>}

          <div className={`text-resp-lg ${bodyColour}`}>{props.body}</div>
        </div>
      )
    case "2xl-stacked-centre":
      return (
        <div className={`p-3 sm:py-6 rounded-xl ${hoverClass} ${props.itemClass ?? ""} text-center`}>
          {props.icon && (
            <div className={`flex mx-auto h-fit w-fit items-center justify-center rounded-lg ${props.iconBgColour ?? ""}`}>
              {typeof props.icon === "string" ? (
                <div className="relative h-6 sm:h-8 md:h-10 lg:h-14 w-6 sm:w-8 md:w-10 lg:w-14 m-2">
                  <Image src={props.icon} alt="" fill />
                </div>
              ) : (
                <props.icon className={`h-6 sm:h-8 md:h-10 lg:h-14 w-6 sm:w-8 md:w-10 lg:w-14 m-2 `} aria-hidden="true" />
              )}
            </div>
          )}
          <dt className="text-resp-lg font-semibold leading-7">
            <span className={`${titleClass}`}>{props.title}</span>
          </dt>
          <dd className={`sm:mt-2 text-resp-base leading-7 ${bodyColour}`}>{props.body}</dd>
        </div>
      )
    case "3xl-stacked-centre":
      return (
        <div className={`p-4 px-6 sm:py-6 rounded-xl ${hoverClass} ${props.itemClass ?? ""} text-center`}>
          {props.icon && (
            <div className={`flex mx-auto h-fit lg:h-24 w-fit items-center justify-center rounded-lg ${props.iconBgColour ?? ""}`}>
              {typeof props.icon === "string" ? (
                <div className={`relative ${props.iconSize ?? "h-10 sm:h-12 md:h-16 lg:h-20 w-10 sm:w-12 md:w-16 lg:w-20"} m-2 `}>
                  <Image src={props.icon} alt="" fill />
                </div>
              ) : (
                <props.icon className={`h-10 sm:h-12 md:h-16 lg:h-20 w-10 sm:w-12 md:w-16 lg:w-20 m-2 ${iconColour}`} aria-hidden="true" />
              )}
            </div>
          )}
          <dt className="text-resp-lg font-semibold leading-7">
            <span className={`${titleClass}`}>{props.title}</span>
          </dt>
          <dd className={`sm:mt-2 text-resp-base ${bodyColour}`}>{props.body}</dd>
        </div>
      )

    default:
      return (
        <div className={`rounded-xl p-4 ${hoverClass} ${props.itemClass ?? ""} text-start`}>
          {props.icon && (
            <div className={`mb-2 flex h-fit w-fit items-center rounded-md sm:mb-4 ${props.iconBgColour ?? "bg-green"}`}>
              {typeof props.icon === "string" ? (
                <div className="relative m-2 h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 lg:h-14 lg:w-14">
                  <Image src={props.icon} alt="" fill />
                </div>
              ) : (
                <props.icon className={`m-2 h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10`} />
              )}
            </div>
          )}

          {typeof props.title === "string" ? <h2 className={titleClass}>{props.title}</h2> : props.title && <div className={titleClass}>{props.title}</div>}

          <div className={`text-resp-lg ${bodyColour}`}>{props.body}</div>
        </div>
      )
  }
}

export default FeatureItem
